import { randomUUID } from 'node:crypto'
import { existsSync, readFileSync, writeFileSync, mkdirSync } from 'node:fs'
import { join } from 'node:path'

export interface InstanceInfo {
  instanceId: string
  machineId: string
}

const INSTANCE_FILE = 'instance.id'

/** Cached result — only read/generate once per process */
let cached: InstanceInfo | null = null

async function getMachineId(): Promise<string> {
  try {
    const mod = await import('node-machine-id')
    const machineIdSync = mod.machineIdSync ?? mod.default?.machineIdSync
    return machineIdSync(false)
  } catch {
    // Fallback: if node-machine-id fails (Docker without dbus, etc.)
    return 'unknown'
  }
}

function isValid(data: unknown): data is InstanceInfo {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as any).instanceId === 'string' &&
    (data as any).instanceId.length > 0 &&
    typeof (data as any).machineId === 'string'
  )
}

/**
 * Read or create instance identification.
 * File: `{dataDir}/instance.id` (JSON with instanceId + machineId).
 * Generated before migrations, independent of DB.
 */
export async function getOrCreateInstanceInfo(dataDir: string): Promise<InstanceInfo> {
  if (cached) return cached

  const filePath = join(dataDir, INSTANCE_FILE)

  // Try reading existing
  if (existsSync(filePath)) {
    try {
      const raw = JSON.parse(readFileSync(filePath, 'utf-8'))
      if (isValid(raw)) {
        cached = raw
        return cached
      }
    } catch {
      // Corrupted file — regenerate
    }
  }

  // Generate new
  const info: InstanceInfo = {
    instanceId: randomUUID(),
    machineId: await getMachineId(),
  }

  mkdirSync(dataDir, { recursive: true })
  writeFileSync(filePath, JSON.stringify(info, null, 2), { mode: 0o600 })

  cached = info
  return info
}

/**
 * Get cached instance info (null if not yet initialized).
 * Use after server startup for synchronous access.
 */
export function getInstanceInfo(): InstanceInfo | null {
  return cached
}

/** Reset cache — for testing only */
export function _resetInstanceInfoCache(): void {
  cached = null
}
